// ── WHERE THE PRODUCTION FIGURE CAME FROM, AND HOW MANY ACRES IT COVERS ──────
//
// Kevin, 2026-10-03, on the simplified claim page: *"I just want to see clearly insured
// acres and JD harvested acres if the source is JD data. I want to label whether it's scale
// tickets, bin measurements, JD data, etc."*
//
// Both halves of that are facts the app already holds and never put on one line. The claim
// page printed 78,057 bu four times and said "yield monitor" once, eight screens away from
// the acres it was measured over.
//
// ⚠ AN ABSENT CHANNEL IS LISTED, NOT OMITTED. "No scale tickets on file" and "scale tickets:
// 0 bu" are different statements, and a channel left off the list reads as the second one.
// This is the named-absence rule the briefs already follow, applied to the five places a
// bushel can come from. It is also the half that makes the list checkable: a reader can see
// that the monitor won because nothing outranked it, rather than taking it on trust.
//
// ⚠ AND THE RMA CODE IS READ, NEVER CHOSEN. `productionRecord.EVIDENCE` already states which
// code each channel carries and, for the two it cannot resolve, which codes are the
// candidates — bin measurement is B or E depending on who measured it, an appraisal is N, K
// or R depending on the policy and allocation. Those render as a CHOICE, in the amber this
// app uses for "ask", never as a code we picked. A code on a federal form that nobody chose
// is the invented-explanation failure this repo opens with.
//
// ⚠ AND A FARM WITH NO MACHINE DATA HAS NO HARVESTED ACRES — not zero of them. Anderson Farms
// Dairy insures 340.6 wheat acres and owns no John Deere connection; subtracting gives
// "340.6 acres the combine did not cover", which is a statement about a combine that was
// never there. `acreBasis` returns nulls and says so, and the screen prints an em dash with
// the reason beside it.

"use strict";

const PR = require("./productionRecord.js");

// The order the channels are PRINTED in, which is not the order they are stored. The one
// actually being reported leads (the reader's first question is "where did this number come
// from"); the rest follow in the order RMA's own form lists them, so a reader holding the
// paper can go down both lists together.
const PRINT_ORDER = Object.freeze([
  "scale_tickets",
  "jd_yield",
  "bin_measurement",
  "fed_on_farm",
  "appraisal",
]);

// ⚠ A FARMER'S OWN ESTIMATE IS DELIBERATELY NOT A CHANNEL HERE — excluded by Kevin,
// 2026-10-06, and written down because its absence otherwise reads as an oversight.
//
// He had listed the production sources as "appraisal, bin measurements, scale tickets, JD
// ops data, manually uploaded yield data, or even estimates from a farmer", so a reader
// counting five channels against that six is one edit away from helpfully adding the sixth.
//
// THE REASON IS SOURCED, NOT A PREFERENCE. Every channel above carries the RMA Production
// Record Type it would be FILED under, and that vocabulary is enumerated on the form itself
// — A..H, I..Q, Z, R, T, # (productionRecord.js, read verbatim off page 9 of the NAU
// Schedule of Insurance / Production Report). A grower's verbal estimate is not among them,
// because it is not an acceptable production record. There is no code to give it.
//
// So adding it as a peer would put an unfilable number in a list whose entire purpose is to
// say which record each figure would be filed as, and the eventual cost is somebody filing
// a grower's guess as production. If it is ever wanted it belongs somewhere else entirely —
// a working/planning figure, explicitly marked as never entering the production report.
//
// MANUALLY UPLOADED YIELD DATA is a different case and is simply NOT BUILT YET: it has real
// candidate codes (D if it is a monitor export, G "Field Harvest Record" or H "Other" if it
// was hand-recorded), so it would arrive as a `needsHuman` channel like bin_measurement,
// which asks rather than picking.

// What a channel with nothing in it SAYS. Each is specific, because "none" on a channel we
// read from a table and "none" on a channel somebody types are different problems with
// different fixes — one is a missing import, the other is an unanswered question.
const ABSENT_TEXT = Object.freeze({
  scale_tickets: "none on file",
  jd_yield: "no machine data",
  bin_measurement: "nothing entered",
  fed_on_farm: "nothing entered",
  appraisal: "none",
});

const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * Every place a bushel could have come from, present or not, each with its RMA record type.
 *
 * `evidence` is `buildProductionEvidence`'s result; `chosenKey` is the basis the reader
 * picked ("documented" | "monitor" | null for the default). Returns rows in print order with
 * `inUse` set on the ones the chosen basis would actually file.
 */
function buildProductionSources(evidence, chosenKey) {
  const lanes = (evidence && Array.isArray(evidence.lanes) ? evidence.lanes : []);
  const byKind = new Map();
  for (const l of lanes) if (l && l.kind) byKind.set(l.kind, l);

  // Which channels the chosen basis draws on. The monitor and the documented records are
  // mutually exclusive — the same harvest weighed twice — and an appraisal ADDS to whichever
  // was chosen, because unharvested acres never reached a scale. That is `productionReportLine`'s
  // rule and it is READ here rather than restated: a second copy is how the panel comes to
  // disagree with the figure it is describing.
  // ⚠ A MONITOR THAT IS NOT ON FILE CANNOT BE WHAT WOULD BE REPORTED. The default arm used to
  // ask only whether documented records were ABSENT, so it fell to the monitor on any farm
  // without tickets — including farms with no machine pass at all, and farms with nothing on
  // file whatsoever. Measured by driving this rule 2026-10-07: an appraisal-only farm
  // (ROGERS FARMS, 60,438 bu appraised, no JD data) reported `monitorChosen: true`, and so did
  // a farm with NO lanes at all, which made the panel's "Nothing is on file for this crop year
  // yet" sentence UNREACHABLE. Kevin saw the result on Rogers' card: "With none on file, the
  // monitor is what would be reported", on a farm whose own John Deere row reads "no machine
  // data" two lines above it.
  //
  // An explicit `chosenKey === "monitor"` still wins — that is the engine stating a choice,
  // not this rule inferring one from an absence.
  const monitorPresent = byKind.has("jd_yield");
  const monitorChosen = chosenKey === "monitor"
    || (chosenKey == null && monitorPresent && !["scale_tickets", "bin_measurement", "fed_on_farm"].some((k) => byKind.has(k)));
  const usedKinds = new Set(
    monitorChosen ? ["jd_yield"] : ["scale_tickets", "bin_measurement", "fed_on_farm"]
  );

  const rows = PRINT_ORDER.map((kind) => {
    const spec = PR.EVIDENCE[kind] || {};
    const lane = byKind.get(kind) || null;
    const qty = lane ? n(lane.quantity) : null;
    return {
      kind,
      // The channel's own name, from the registry — so a channel renamed there is renamed
      // here, and the panel cannot drift from the vocabulary the report is filed under.
      label: spec.label || kind,
      note: spec.note || null,
      present: !!lane,
      quantity: qty,
      unit: (lane && lane.unit) || (evidence && evidence.unit) || null,
      // `code` where RMA's form has exactly one; `candidates` where it does not. NEVER both,
      // and never a first-candidate default.
      code: spec.code || null,
      candidates: spec.code ? null : (spec.candidates || null),
      needsHuman: !!spec.needsHuman,
      absentText: ABSENT_TEXT[kind] || "none",
      // Only a channel that is BOTH present and drawn on by the chosen basis is in use. A
      // present channel the basis does not draw on (scale tickets beside a monitor read) is
      // listed with its figure and NOT marked, which is exactly the case a reader must see.
      inUse: !!lane && usedKinds.has(kind),
    };
  });

  const used = rows.filter((r) => r.inUse);
  return {
    rows,
    // The channels actually being filed, so a caller can label the figure at the top of the
    // page with the same words as the row below it.
    inUse: used,
    // One channel in use is the common case and gets a name; several (sold + stored + fed) is
    // a sum and says so rather than naming whichever sorted first.
    label: used.length === 1 ? used[0].label : used.length > 1 ? `${used.length} farm records` : null,
    code: used.length === 1 ? used[0].code : null,
    // Reported so a screen can say "nothing outranked the monitor" rather than implying it.
    documentedAvailable: ["scale_tickets", "bin_measurement", "fed_on_farm"].some((k) => byKind.has(k)),
    monitorChosen,
    // Reported as FACTS so the screen can phrase the fourth case rather than falling through
    // to "nothing is on file" on a farm that holds an appraisal. An appraisal is production
    // for the acres it covers; it simply is not a harvested-production record, which is why
    // it is neither `documentedAvailable` nor the monitor.
    monitorPresent,
    appraisalPresent: byKind.has("appraisal"),
  };
}

/**
 * Insured acres against the acres a combine actually covered.
 *
 * `fc` is `buildFieldCoverage`'s result. Returns nulls — never zeros — where this farm has no
 * machine data at all, because a subtraction then measures a combine that was never there.
 */
function acreBasis(fc) {
  const t = (fc && fc.totals) || null;
  if (!t) return { insuredAcres: null, harvestedAcres: null, uncoveredAcres: null, hasMachineData: false, why: "no field coverage computed" };

  const insured = n(t.insuredAcres);
  // ⚠ ONLY A GRIDDED PASS STATES ACRES FOR A FIELD. An unmapped pass's bushels are real and
  // its acreage cannot be attributed to one field, so those acres are NOT summed here — they
  // are counted separately, and a screen that hid them would under-report the harvest.
  const rows = (fc && Array.isArray(fc.rows) ? fc.rows : []);
  let harvested = 0, placed = 0;
  for (const r of rows) {
    const a = r && r.harvested ? n(r.harvested.acres) : null;
    if (a != null) { harvested += a; placed++; }
  }

  // Does this farm have ANY machine pass for this crop and year? A farm with none is not a
  // farm whose combine missed every acre.
  const anyPass = placed > 0 || (n(t.unmapped) || 0) > 0 || (fc && (fc.unmappedPassCount || 0) > 0);
  if (!anyPass) {
    return {
      insuredAcres: insured, harvestedAcres: null, uncoveredAcres: null,
      hasMachineData: false, placedFields: 0, unmappedAcres: null,
      why: "no John Deere pass on file for this crop and year, so there are no harvested acres to compare against",
    };
  }

  const h = +harvested.toFixed(1);
  return {
    insuredAcres: insured,
    harvestedAcres: h,
    // Insured minus covered. Only computed where both halves exist, and it is a DIFFERENCE,
    // never a judgment — a field harvested by a neighbour's combine, or a pass whose map did
    // not arrive, lands here too.
    uncoveredAcres: insured == null ? null : +(insured - h).toFixed(1),
    hasMachineData: true,
    placedFields: placed,
    // Acres whose pass exists but could not be placed on a field — reported, never folded in.
    unmappedAcres: n(t.unmappedAcres),
    why: null,
  };
}

module.exports = { buildProductionSources, acreBasis, PRINT_ORDER, ABSENT_TEXT };
